import React, { useState } from "react";

import Dialog from "@busbud/design-system-components/dist/Dialog";
import Divider from "@busbud/design-system-components/dist/Divider";

import type { ErrorModalButtonType } from "@app/helpers/api-error";

import type { ButtonConfig } from "./alert-button";
import type { CancelDialogBodyProps } from "./dialog-body";
import type { CancelDialogTitleProps } from "./dialog-title";

import { useStyles } from "./cancel-dialog.styles";
import { CancelDialogBody } from "./dialog-body";
import { CancelDialogFooter } from "./dialog-footer";
import { CancelDialogTitle } from "./dialog-title";

type CancelDialogDialogStylesProps = Partial<
  Record<"content" | "box" | "title" | "action_button", string>
>;

type Props = {
  actionHandler?: (
    button_type: ErrorModalButtonType,
    action_name?: string
  ) => void;
  message?: React.ReactNode;
  cta_button_config?: ButtonConfig;
  primary_button_config?: ButtonConfig;
  secondary_button_config?: ButtonConfig;
  fullScreen?: boolean;
  classes?: CancelDialogDialogStylesProps;
  title?: string | null | undefined;
} & Partial<CancelDialogBodyProps> &
  Partial<CancelDialogTitleProps> &
  Omit<React.ComponentProps<typeof Dialog>, "open">;

export const CancelDialog: React.FC<Props> = props => {
  const {
    actionHandler,
    cta_button_config,
    primary_button_config,
    secondary_button_config,
    message,
    tip,
    title,
    title_icon,
    classes: external_classes,
    onClose,
    ...others
  } = props;
  const dialog_styles = useStyles();
  const [is_open, setIsOpen] = useState(true);
  const classes = { ...dialog_styles, ...external_classes };
  const is_title_visible = !!title;
  const is_footer_visible =
    cta_button_config || primary_button_config || secondary_button_config;

  const handleButtonClick = (
    button_type: ErrorModalButtonType,
    action_name: string | undefined
  ) => {
    if (actionHandler) {
      actionHandler(button_type, action_name);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    onClose?.();
  };

  return (
    <Dialog
      classes={{
        box: classes.box
      }}
      open={is_open}
      maxWidth="xs"
      onClose={handleClose}
      {...others}
    >
      {!!is_title_visible && (
        <>
          <CancelDialogTitle title_icon={title_icon}>{title}</CancelDialogTitle>
          <Divider />
        </>
      )}
      <CancelDialogBody tip={tip}>{message}</CancelDialogBody>

      {!!is_footer_visible && (
        <>
          <Divider />
          <CancelDialogFooter
            cta_button_config={cta_button_config}
            primary_button_config={primary_button_config}
            secondary_button_config={secondary_button_config}
            handleButtonClick={handleButtonClick}
          />
        </>
      )}
    </Dialog>
  );
};
